// Client-side encryption utilities for sensitive data
import CryptoJS from 'crypto-js';

// Use environment variable for encryption key in production
const ENCRYPTION_KEY =
  import.meta.env.VITE_ENCRYPTION_KEY ||
  'fallback-dev-key-change-in-production';

export const encryptData = (data: string): string => {
  try {
    return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

export const decryptData = (encryptedData: string): string => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data');
  }
};

// Hash email for transmission (one-way)
export const hashEmail = (email: string): string => {
  return CryptoJS.SHA256(email.toLowerCase().trim()).toString();
};

// Create a secure payload for login
export const createSecureLoginPayload = (email: string, password: string) => {
  const timestamp = Date.now();
  const nonce = CryptoJS.lib.WordArray.random(16).toString();

  return {
    emailHash: hashEmail(email),
    email: encryptData(email), // Encrypted for server decryption
    password: encryptData(password), // Encrypted password
    timestamp,
    nonce,
  };
};

// Clear sensitive data from memory
export const secureClearString = (str: string): void => {
  // In JavaScript, we can't truly clear memory, but we can overwrite references
  if (typeof str === 'string') {
    str = '';
  }
};

// Secure storage utilities
export const secureStorageSet = (key: string, value: string): void => {
  try {
    const encryptedValue = encryptData(value);
    localStorage.setItem(key, encryptedValue);
  } catch (error) {
    console.error('Secure storage error:', error);
    // Fallback to regular storage if encryption fails
    localStorage.setItem(key, value);
  }
};

export const secureStorageGet = (key: string): string | null => {
  try {
    const encryptedValue = localStorage.getItem(key);
    if (!encryptedValue) return null;

    return decryptData(encryptedValue);
  } catch (error) {
    console.error('Secure storage retrieval error:', error);
    // Fallback to regular storage
    return localStorage.getItem(key);
  }
};

export const secureStorageRemove = (key: string): void => {
  localStorage.removeItem(key);
};
